import React, { useEffect, useRef, useState } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import esriId from '@arcgis/core/identity/IdentityManager';
import esriConfig from '@arcgis/core/config';

const ArcGISFeatureMap = ({ 
  featureServiceUrl = "https://services2.arcgis.com/w0DLXtWY5Mf6iZvz/arcgis/rest/services/service_da5ef023332a450c98922c2afc453ce5/FeatureServer",
  layerId = 0,
  whereClause = "1=1",
  height = '600px',
  token = null
}) => {
  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [featureCount, setFeatureCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mapRef.current || !token) return;

    // Destroy previous view if exists
    if (viewRef.current) {
      viewRef.current.destroy();
      viewRef.current = null;
    }

    const initMap = async () => {
      try {
        // Register token BEFORE creating anything
        esriId.registerToken({
          server: "https://services2.arcgis.com/w0DLXtWY5Mf6iZvz/arcgis",
          token: token
        });

        // Create map
        const map = new Map({
          basemap: 'streets-navigation-vector'
        });

        // Create view centered on Cádiz
        const view = new MapView({
          container: mapRef.current,
          map: map,
          center: [-6.2885, 36.5297], // Cádiz
          zoom: 14,
          popup: {
            dockEnabled: true,
            dockOptions: {
              buttonEnabled: false,
              breakpoint: false
            }
          }
        });

        viewRef.current = view;

        // Wait for view to be ready
        await view.when();

        // Define popup template with correct field names
        const popupTemplate = {
          title: "🎫 Recogida de Enseres",
          content: [{
            type: "fields",
            fieldInfos: [
              { fieldName: "nombre_reclamante", label: "👤 Ciudadano" },
              { fieldName: "telefono", label: "📞 Teléfono" },
              { fieldName: "mail", label: "📧 Email" },
              { fieldName: "direccion_", label: "📍 Dirección" },
              { fieldName: "lugar", label: "🗺️ Zona" },
              { fieldName: "Bloque", label: "🏢 Bloque" },
              { fieldName: "Piso", label: "🚪 Piso" },
              { fieldName: "procedencia", label: "📱 Procedencia" },
              { fieldName: "estado", label: "📊 Estado" },
              { fieldName: "recibida_por", label: "👥 Recibida por" }
            ]
          }]
        };

        // Define renderer by estado
        const uniqueValueRenderer = {
          type: "unique-value",
          field: "estado",
          defaultSymbol: {
            type: "simple-marker",
            color: [128, 128, 128, 0.8],
            size: "10px",
            outline: { color: [255, 255, 255], width: 1.5 }
          },
          uniqueValueInfos: [
            {
              value: "Pendiente",
              symbol: { type: "simple-marker", color: [255, 165, 0, 0.9], size: "10px", outline: { color: [255, 255, 255], width: 1.5 } }
            },
            {
              value: "Notificada",
              symbol: { type: "simple-marker", color: [138, 43, 226, 0.9], size: "10px", outline: { color: [255, 255, 255], width: 1.5 } }
            },
            {
              value: "Asignada",
              symbol: { type: "simple-marker", color: [0, 123, 255, 0.9], size: "10px", outline: { color: [255, 255, 255], width: 1.5 } }
            },
            {
              value: "Realizada",
              symbol: { type: "simple-marker", color: [40, 167, 69, 0.9], size: "10px", outline: { color: [255, 255, 255], width: 1.5 } }
            },
            {
              value: "Cancelada",
              symbol: { type: "simple-marker", color: [220, 53, 69, 0.9], size: "10px", outline: { color: [255, 255, 255], width: 1.5 } }
            }
          ]
        };

        // Build URL with token
        const layerUrl = `${featureServiceUrl}/${layerId}`;

        // Create Feature Layer
        const featureLayer = new FeatureLayer({
          url: layerUrl,
          outFields: ["*"],
          popupTemplate: popupTemplate,
          renderer: uniqueValueRenderer,
          definitionExpression: whereClause
        });

        layerRef.current = featureLayer;
        map.add(featureLayer);

        // Wait for layer
        await featureLayer.when();
        
        setIsLoaded(true);
        
        // Query feature count
        try {
          const count = await featureLayer.queryFeatureCount({ where: whereClause });
          setFeatureCount(count);
        } catch (e) {
          console.warn('Could not get feature count:', e);
        }

        // Zoom to layer extent
        try {
          const response = await featureLayer.queryExtent({ where: whereClause });
          if (response.extent) {
            view.goTo(response.extent.expand(1.2), { duration: 1000 });
          }
        } catch (e) {
          console.warn('Could not zoom to extent:', e);
        }

      } catch (err) {
        console.error('Map initialization error:', err);
        setError(err.message);
      }
    };

    initMap();

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [featureServiceUrl, layerId, whereClause, token]);

  if (!token) {
    return (
      <div className="w-full rounded-lg border border-yellow-200 bg-yellow-50 p-8 text-center" style={{ height }}>
        <div className="text-4xl mb-4">⏳</div>
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Esperando token de ArcGIS...</h3>
        <p className="text-sm text-yellow-600">Obteniendo credenciales de acceso</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full rounded-lg border border-red-200 bg-red-50 p-8 text-center" style={{ height }}>
        <div className="text-4xl mb-4">⚠️</div>
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error cargando capa de ArcGIS</h3>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-gray-200 shadow-sm">
      <div ref={mapRef} style={{ width: '100%', height: height }} />
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Cargando capa de ArcGIS...</p>
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-200">
        <p className="text-xs font-semibold mb-2 text-gray-700">📋 Leyenda</p>
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500 shadow-sm"></span>
            <span className="text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-purple-500 shadow-sm"></span>
            <span className="text-gray-600">Notificada</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-blue-500 shadow-sm"></span>
            <span className="text-gray-600">Asignada</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500 shadow-sm"></span>
            <span className="text-gray-600">Realizada</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500 shadow-sm"></span>
            <span className="text-gray-600">Cancelada</span>
          </div>
        </div>
      </div>
      {/* Feature count */}
      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-gray-200">
        <p className="text-xs font-semibold text-gray-700">
          📍 {featureCount.toLocaleString()} registros
        </p>
      </div>
    </div>
  );
};

export default ArcGISFeatureMap;
